// Sole producer of the canonical ApiError shape; all other code consumes it structurally.

import type { ApiError } from './api-error';
import { isApiError } from './api-error';

interface HttpErrorLike {
  status: number;
  statusText?: string;
  error?: unknown;
}

export function normalizeError(raw: unknown): ApiError {
  if (isApiError(raw)) {
    return raw;
  }

  if (raw instanceof Error) {
    return {
      message: 'Unable to connect to the server. Please check your connection.',
      status: 0,
      originalError: raw,
    };
  }

  if (typeof raw === 'string') {
    return {
      message: raw || 'Unknown error',
      status: 0,
      originalError: raw,
    };
  }

  if (raw === null || typeof raw !== 'object') {
    return {
      message: 'Unknown error',
      status: 0,
      originalError: raw,
    };
  }

  const asObj = raw as Record<string, unknown>;
  const status = typeof asObj['status'] === 'number' ? asObj['status'] : 0;
  const statusText =
    typeof asObj['statusText'] === 'string' ? asObj['statusText'] : '';
  const body: unknown = 'error' in asObj ? asObj['error'] : raw;

  const httpLike: HttpErrorLike = { status, statusText, error: body };
  const message = extractMessage(httpLike);
  const fieldErrors = status === 422 ? extractFieldErrors(body) : undefined;

  const result: ApiError = {
    message,
    status,
    originalError: body,
  };
  if (fieldErrors) {
    result.fieldErrors = fieldErrors;
  }
  return result;
}

export function apiErrorMessage(error: unknown, fallback: string): string {
  return isApiError(error) ? error.message : fallback;
}

// The machine-readable discriminator the backend attaches to an errors[] entry
// (e.g. a linked-transactions 409 carries `reason: 'linked_transactions'`), so a
// caller can branch recovery on it instead of guessing from status or message.
export function apiErrorReason(error: unknown): string | undefined {
  if (!isApiError(error)) return undefined;
  const body = error.originalError;
  if (body === null || typeof body !== 'object') return undefined;
  const errors = (body as Record<string, unknown>)['errors'];
  if (!Array.isArray(errors) || errors.length === 0) return undefined;
  const first = errors[0];
  if (first === null || typeof first !== 'object') return undefined;
  const reason = (first as Record<string, unknown>)['reason'];
  return typeof reason === 'string' ? reason : undefined;
}

function extractMessage(error: HttpErrorLike): string {
  if (error.status === 0) {
    return 'Unable to connect to the server. Please check your connection.';
  }

  const body: unknown = error.error;

  if (typeof body === 'string') {
    return body || error.statusText || 'Unknown error';
  }

  if (body !== null && typeof body === 'object') {
    const fromObject = extractMessageFromObject(body as Record<string, unknown>);
    if (fromObject !== null) return fromObject;
  }

  return error.statusText || 'Unknown error';
}

function extractMessageFromObject(obj: Record<string, unknown>): string | null {
  const fromErrorField = extractFromErrorField(obj);
  if (fromErrorField !== null) return fromErrorField;

  if (typeof obj['message'] === 'string') return obj['message'];
  if (typeof obj['detail'] === 'string') return obj['detail'];

  return extractFromErrorsMap(obj);
}

function extractFromErrorField(obj: Record<string, unknown>): string | null {
  if (!('error' in obj)) return null;
  const err = obj['error'];
  if (typeof err === 'string') return err;
  if (err !== null && typeof err === 'object') {
    const nested = err as Record<string, unknown>;
    if (typeof nested['error'] === 'string') return nested['error'];
    if (typeof nested['message'] === 'string') return nested['message'];
  }
  return null;
}

function extractFromErrorsMap(obj: Record<string, unknown>): string | null {
  if (!('errors' in obj) || obj['errors'] === null || typeof obj['errors'] !== 'object') {
    return null;
  }

  if (Array.isArray(obj['errors'])) {
    return extractFromErrorsArray(obj['errors']);
  }

  const errors = obj['errors'] as Record<string, unknown>;
  const messages: string[] = [];
  for (const key of Object.keys(errors)) {
    const val = errors[key];
    if (val) {
      messages.push(`${key}: ${formatErrorValue(val)}`);
    }
  }
  return messages.length > 0 ? messages.join('; ') : null;
}

// The backend wraps every error in an `errors` ARRAY: plain strings for
// service/authorization failures, `{field, messages}` entries for model
// validation (422), and `{param, value, message}` entries for bad query
// params (422).
function extractFromErrorsArray(errors: unknown[]): string | null {
  const messages: string[] = [];
  for (const entry of errors) {
    if (typeof entry === 'string') {
      if (entry) messages.push(entry);
      continue;
    }
    if (entry === null || typeof entry !== 'object') continue;

    const obj = entry as Record<string, unknown>;
    if (typeof obj['field'] === 'string' && Array.isArray(obj['messages'])) {
      messages.push(`${obj['field']}: ${obj['messages'].map(String).join(', ')}`);
    } else if (typeof obj['message'] === 'string') {
      messages.push(obj['message']);
    }
  }
  return messages.length > 0 ? messages.join('; ') : null;
}

function formatErrorValue(val: unknown): string {
  if (Array.isArray(val)) return val.join(', ');
  if (typeof val === 'string') return val;
  return JSON.stringify(val);
}

function extractFieldErrors(body: unknown): Record<string, string[]> | undefined {
  if (body === null || typeof body !== 'object') {
    return undefined;
  }

  const obj = body as Record<string, unknown>;
  if (!('errors' in obj) || obj['errors'] === null || typeof obj['errors'] !== 'object') {
    return undefined;
  }

  if (Array.isArray(obj['errors'])) {
    return extractFieldErrorsFromArray(obj['errors']);
  }

  const errorsObj = obj['errors'] as Record<string, unknown>;
  const result: Record<string, string[]> = {};
  for (const key of Object.keys(errorsObj)) {
    const val = errorsObj[key];
    if (Array.isArray(val)) {
      const strs = val.map(String);
      if (strs.length > 0) {
        result[key] = strs;
      }
    } else if (val !== null && val !== undefined && val !== '') {
      result[key] = [formatErrorValue(val)];
    }
  }

  return Object.keys(result).length > 0 ? result : undefined;
}

// Only `{field, messages}` entries are field-scoped; plain strings and
// `{param, value, message}` entries carry no form field to attach to.
function extractFieldErrorsFromArray(errors: unknown[]): Record<string, string[]> | undefined {
  const result: Record<string, string[]> = {};
  for (const entry of errors) {
    if (entry === null || typeof entry !== 'object') continue;

    const obj = entry as Record<string, unknown>;
    if (typeof obj['field'] !== 'string' || !Array.isArray(obj['messages'])) continue;

    const messages = obj['messages'].map(String).filter(Boolean);
    if (messages.length > 0) {
      result[obj['field']] = [...(result[obj['field']] ?? []), ...messages];
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
}
